import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, Observable, switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { environment } from 'src/environment/environment';

// ── Master entities ──────────────────────────────────────────────────────────
import { HospitalMasterDTO, HospitalMasterReadByIdRequestDTO } from 'src/app/hospital/BackOffice/hospital-master/hospital-master.interface';
import { HospitalMasterService } from 'src/app/hospital/BackOffice/hospital-master/hospital-master.service';
import { SchoolMasterDTO, SchoolMasterReadByIdRequestDTO } from 'src/app/School/BackOffice/school-master/school-master.interface';
import { SchoolMasterService } from 'src/app/School/BackOffice/school-master/school-master.service';
import { SocietyDTO, SocietyReadByIdRequestDTO } from 'src/app/housing-society/Interface & Service/society/society.interface';
import { SocietyService } from 'src/app/housing-society/Interface & Service/society/society.service';

// ── UAM services ─────────────────────────────────────────────────────────────
import { HospitalUserAffiliationService } from 'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service';
import { HospitalUserAffiliationCreateRequestDTO } from 'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.interface';
import { UserAffiliationMappingService } from 'src/app/School/SchoolUserMapping/user-affiliation-mapping.service';
import { UserAffiliationMappingCreateRequestDTO } from 'src/app/School/SchoolUserMapping/user-affiliation-mapping.interface';
import { UserAffiliationMappingService as SocietyUAMService } from 'src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.service';
import { SMUserAffiliationMappingCreateRequestDTO } from 'src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.interface';

// ── Student admission (school) ───────────────────────────────────────────────
import { StudentAdmissionService } from 'src/app/School/BackOffice/student-admission/student-admission.service';
import { StudentAdmissionCreateRequestDTO } from 'src/app/School/BackOffice/student-admission/student-admission.interface';

// ── UserMaster service ────────────────────────────────────────────────────────
import { UsersService } from 'src/app/master/users/users.service';
import { UserMasterCreateRequestDTO } from 'src/app/master/users/users.interface';

// ── Class Master ──────────────────────────────────────────────────────────────
import { ClassMasterService } from 'src/app/School/BackOffice/class-master/class-master.service';
import { ClassMasterDTO } from 'src/app/School/BackOffice/class-master/class-master.interface';

// ── Department & Society Master Services ──────────────────────────────────────
import { DepartmentMasterService } from 'src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-department/hospital-department.service';
import { BuildingService } from 'src/app/housing-society/Interface & Service/building/building.service';
import { FlatService } from 'src/app/housing-society/Interface & Service/flat/flat.service';

// ── OTP (plain JSON, no encryption) ───────────────────────────────────────────
import { HttpClient } from '@angular/common/http';

// ── Encryption & Signature ────────────────────────────────────────────────────
import { EncryptionServiceService } from 'src/app/Common/EncryptionService/encryption-service.service';
import { UserPermissionService } from 'src/app/Common/UserPermission/userPermission.service';
import { SignatureRequestDTO, SignatureResponseDTO } from 'src/app/Common/UserPermission/userpermission.interface';
import { UserLogin_GenerateMobileOtp, UserLogin_ValidateMobileOtp } from 'src/app/GlobalVariable';

interface GenerateOtpRequest  { mobileNo: string; emailId?: string; }
interface GenerateOtpResponse { statusCode: number; message: string; expiryTime?: string; }
interface ValidateOtpRequest  { mobileNo: string; otp: string; }
interface ValidateOtpResponse { statusCode: number; message: string; verifiedAt?: string; }

interface EncryptedExternalBody { EncData: string; TokenKey: string; }

/** Wizard steps: pick a role -> one form (OTP is inline) -> success */
type Step = 'role' | 'form' | 'success';

/** Flat member designation choices (society) */
type FlatDesignation = 'Owner' | 'Co-Owner' | 'Other';

/** Device the visitor is on, used to pick the right app store */
type Platform = 'ios' | 'android' | 'other';

interface ConfettiPiece { x: number; y: number; r: number; c: string; d: number; }

interface RoleOption { value: string; label: string; icon: string; desc: string; }

interface Highlight { icon: string; title: string; text: string; }

const DEFAULT_FLAT_DESIGNATION_TEXT = 'Flat Member';

/** Seconds the success message stays visible on a phone before the store opens */
const STORE_REDIRECT_SECONDS = 3;

@Component({
  selector: 'app-self-onboarding',
  templateUrl: './self-onboarding.component.html',
  styleUrls: ['./self-onboarding.component.css'],
  animations: [
    // Step change (role -> form -> success)
    trigger('stepAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(14px)' }),
        animate('320ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ opacity: 1, transform: 'none' }))
      ])
    ]),
    // OTP boxes + "add more details" panel + designation "Other" box slide open
    trigger('expand', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('280ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class SelfOnboardingComponent implements OnInit, OnDestroy {

  // ── Injections ──────────────────────────────────────────────────────────────
  private router                = inject(Router);
  private route                 = inject(ActivatedRoute);
  private destroyRef            = inject(DestroyRef);
  private toastr                = inject(ToastrService);
  private hospitalMasterService = inject(HospitalMasterService);
  private schoolMasterService   = inject(SchoolMasterService);
  private societyService        = inject(SocietyService);
  private hospitalUAMService    = inject(HospitalUserAffiliationService);
  private schoolUAMService      = inject(UserAffiliationMappingService);
  private societyUAMService     = inject(SocietyUAMService);
  private studentAdmissionService = inject(StudentAdmissionService);
  private usersService          = inject(UsersService);
  private classMasterService    = inject(ClassMasterService);
  private departmentMasterService = inject(DepartmentMasterService);
  private buildingService       = inject(BuildingService);
  private flatService           = inject(FlatService);
  private http                  = inject(HttpClient);
  private encryptionService     = inject(EncryptionServiceService);
  private userPermissionService = inject(UserPermissionService);

  // ── Template refs ───────────────────────────────────────────────────────────
  @ViewChildren('otpBox') otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;

  // ── Route params ────────────────────────────────────────────────────────────
  module: string           = '';
  entityNameWithId: string = '';
  moduleId: number         = 0;
  moduleName: string       = '';

  // ── Page state ──────────────────────────────────────────────────────────────
  isLoading: boolean    = true;
  isSubmitting: boolean = false;

  // ── Entity data ─────────────────────────────────────────────────────────────
  hospitalData: HospitalMasterDTO | null = null;
  schoolData: SchoolMasterDTO     | null = null;
  societyData: SocietyDTO         | null = null;

  // ── Wizard ──────────────────────────────────────────────────────────────────
  currentStep: Step = 'role';
  showMore: boolean = false;

  /** Labels for the progress stepper at the top of the card */
  readonly stepLabels: string[] = ['Role', 'Details', 'Done'];

  /** Index of the current step (0 = role, 1 = form, 2 = success) */
  get stepIndex(): number {
    return this.currentStep === 'role' ? 0 : this.currentStep === 'form' ? 1 : 2;
  }

  /** A step is "done" once we are past it, or when everything is finished */
  isStepDone(i: number): boolean {
    return this.currentStep === 'success' || i < this.stepIndex;
  }

  /** Reasons shown on the left side of the page (desktop) */
  readonly highlights: Highlight[] = [
    { icon: 'bi-lightning-charge-fill', title: 'Quick and simple',   text: 'Three short steps, no paperwork.' },
    { icon: 'bi-shield-check',          title: 'Verified by OTP',    text: 'Your mobile number is confirmed before you submit.' },
    { icon: 'bi-phone-fill',            title: 'Log in with the app', text: 'Use the Nishwik app as soon as you are approved.' }
  ];

  // ── Role selection ──────────────────────────────────────────────────────────
  selectedRole: string = '';

  hospitalRoles: RoleOption[] = [
    { value: 'Doctor',             label: 'Doctor',               icon: '🩺', desc: 'Consult and treat patients' },
    { value: 'Nurse/Receptionist', label: 'Nurse / Receptionist', icon: '💉', desc: 'Patient care and front desk' },
    { value: 'OtherStaff',         label: 'Other Staff',          icon: '🏥', desc: 'Admin, lab, pharmacy and more' }
  ];

  schoolRoles: RoleOption[] = [
    { value: 'TeachingStaff',    label: 'Teaching Staff',     icon: '📚', desc: 'Teachers and faculty' },
    { value: 'NonTeachingStaff', label: 'Non-Teaching Staff', icon: '🗂️', desc: 'Office and support staff' },
    { value: 'Drivers',          label: 'Driver',             icon: '🚌', desc: 'School transport team' },
    { value: 'StudentAdmission', label: 'Student Admission',  icon: '🎓', desc: 'Apply for a new admission' }
  ];

  societyRoles: RoleOption[] = [
    { value: 'SecurityStaff', label: 'Security Staff', icon: '🛡️', desc: 'Gate and patrol team' },
    { value: 'GeneralStaff',  label: 'General Staff',  icon: '🔧', desc: 'General and other society staff' },
    { value: 'FlatMember',    label: 'Flat Member',    icon: '🏠', desc: 'Resident of the society' }
  ];

  genders: string[] = ['Male', 'Female', 'Other'];

  /** Society · flat member designation options */
  readonly flatDesignations: FlatDesignation[] = ['Owner', 'Co-Owner', 'Other'];

  get roles(): RoleOption[] {
    const m = this.module.toLowerCase();
    if (m === 'hospital') return this.hospitalRoles;
    if (m === 'school')   return this.schoolRoles;
    return this.societyRoles;
  }

  get roleLabel(): string {
    return this.roles.find(r => r.value === this.selectedRole)?.label || '';
  }

  get roleIcon(): string {
    return this.roles.find(r => r.value === this.selectedRole)?.icon || '';
  }

  // ── Main form fields ────────────────────────────────────────────────────────
  form = {
    firstName:   '',
    middleName:  '',          // optional
    lastName:    '',
    mobileNo:    '',
    emailId:     '',
    gender:      '',
    dob:         '',
    address:     '',
    pinCode:     '',
    designation: '',
    // Society · flat member designation
    designationType:  'Other' as FlatDesignation,
    designationOther: DEFAULT_FLAT_DESIGNATION_TEXT,
    // Hospital extras
    department:     '',
    specialization: '',
    // Society: building (all roles, optional except flat member) + flat (flat member, required)
    buildingId: 0,
    flatId:     0,
    // Student extras
    classId: 0,
    // Parent info (student admission)
    parentFirstName: '',
    parentLastName:  '',
    parentMobile:    ''
  };

  // ── Mobile + OTP state ──────────────────────────────────────────────────────
  readonly otpLength = 6;
  otpIndexes: number[]  = Array.from({ length: this.otpLength }, (_, i) => i);
  otpDigits: string[]   = Array(this.otpLength).fill('');

  mobileTouched: boolean   = false;
  otpSent: boolean         = false;
  otpVerified: boolean     = false;
  otpError: boolean        = false;
  isSendingOtp: boolean    = false;
  isVerifyingOtp: boolean  = false;
  otpTimer: number         = 0;
  otpIntervalId: any       = null;

  /** The number that gets verified: parent's mobile for student admission, own mobile otherwise */
  get mobileValue(): string {
    return this.isStudentAdmission ? this.form.parentMobile : this.form.mobileNo;
  }
  set mobileValue(v: string) {
    if (this.isStudentAdmission) this.form.parentMobile = v;
    else this.form.mobileNo = v;
  }

  get isMobileValid(): boolean {
    return /^[6-9]\d{9}$/.test(this.mobileValue);
  }

  // ── School student flow ─────────────────────────────────────────────────────
  classes: ClassMasterDTO[] = [];
  classesLoading: boolean   = false;

  // ── Hospital departments flow ───────────────────────────────────────────────
  departments: any[]        = [];
  departmentsLoading: boolean = false;

  // ── Society building & flat flow ───────────────────────────────────────────
  buildings: any[]          = [];
  buildingsLoading: boolean = false;
  flats: any[]              = [];
  flatsLoading: boolean     = false;

  // ── Submission tracking ─────────────────────────────────────────────────────
  createdUserId: number = 0;

  // ── Success screen ──────────────────────────────────────────────────────────
  confetti: ConfettiPiece[] = [];

  /** Device the visitor is on (set in ngOnInit) */
  platform: Platform = 'other';

  /** QR images that failed to load (a plain link is shown for those) */
  qrFailed: Record<string, boolean> = {};

  /** Seconds left before the store opens automatically (0 = no countdown running) */
  redirectIn: number = 0;
  private redirectTimerId: any = null;

  appLinks = {
    playStore: 'https://play.google.com/store/apps/details?id=com.nishwik.app',
    appStore:  'https://apps.apple.com/in/app/nishwik/id6758631916',
    landing:   'https://nishwik.com'
  };

  /** Store link for the detected device (desktop falls back to the website) */
  get storeLink(): string {
    if (this.platform === 'ios')     return this.appLinks.appStore;
    if (this.platform === 'android') return this.appLinks.playStore;
    return this.appLinks.landing;
  }

  get storeName(): string {
    return this.platform === 'ios' ? 'the App Store' : 'Google Play';
  }

  /**
   * QR codes shown on the success screen. They hold the real store link:
   *  - on an Android phone  -> Google Play only
   *  - on an iPhone / iPad  -> App Store only
   *  - on a desktop / laptop -> both (we can't know which phone will scan)
   */
  get qrCodes(): { key: string; label: string; href: string; src: string }[] {
    const play = { key: 'android', label: 'Android · Google Play', href: this.appLinks.playStore };
    const apple = { key: 'ios', label: 'iPhone · App Store', href: this.appLinks.appStore };
    const list = this.platform === 'android' ? [play]
               : this.platform === 'ios'     ? [apple]
               : [play, apple];
    return list.map(q => ({ ...q, src: this.qrSrcFor(q.href) }));
  }

  private qrSrcFor(url: string): string {
    return 'https://api.qrserver.com/v1/create-qr-code/?size=264x264&margin=0&data=' + encodeURIComponent(url);
  }

  // ══════════════════════════════════════════════════════════════════════════════
  ngOnInit(): void {
    this.platform = this.detectPlatform();

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.module           = (params.get('module') ?? '').toLowerCase();
      this.entityNameWithId = params.get('entityNameWithId') ?? '';

      if (!this.entityNameWithId || !this.module) {
        this.router.navigate(['/PageNotFound']);
        return;
      }

      const parsed = this.parseEntityParam(this.entityNameWithId);
      if (!parsed || !parsed.id) {
        this.router.navigate(['/PageNotFound']);
        return;
      }

      this.moduleId   = parsed.id;
      this.moduleName = parsed.name;

      this.loadEntityData();
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.otpIntervalId);
    clearInterval(this.redirectTimerId);
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // DEVICE DETECTION + STORE REDIRECT
  // ══════════════════════════════════════════════════════════════════════════════
  private detectPlatform(): Platform {
    if (typeof navigator === 'undefined') return 'other';

    const nav = navigator as any;
    const ua  = nav.userAgent || '';
    const uaDataPlatform = (nav.userAgentData?.platform || '').toLowerCase();
    const touch = (nav.maxTouchPoints || 0) > 1;

    // iPadOS 13+ (and iOS "request desktop site") reports itself as a Mac, so check for touch too
    if (/iPhone|iPad|iPod/i.test(ua) || (nav.platform === 'MacIntel' && touch)) return 'ios';

    // Android, including Chrome with "Desktop site" turned on (UA then says Linux, but it has touch)
    if (/Android/i.test(ua) || uaDataPlatform === 'android' || (/Linux/i.test(ua) && touch && !/CrOS/i.test(ua))) return 'android';

    return 'other';
  }

  /** Opens the store for this device in the same tab */
  openStore(): void {
    window.location.assign(this.storeLink);
  }

  /** On phones: give the user a moment to read the success message, then open the store */
  private startStoreRedirect(): void {
    if (this.platform === 'other') return;

    clearInterval(this.redirectTimerId);
    this.redirectIn = STORE_REDIRECT_SECONDS;

    this.redirectTimerId = setInterval(() => {
      this.redirectIn--;
      if (this.redirectIn <= 0) {
        this.cancelRedirect();
        this.openStore();
      }
    }, 1000);
  }

  cancelRedirect(): void {
    clearInterval(this.redirectTimerId);
    this.redirectTimerId = null;
    this.redirectIn = 0;
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // URL PARSING + NAME MATCHING
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Splits "Sunrise Hospital_123" (or "sunrise-hospital-123", "Sunrise%20Hospital_123")
   * into { name, id }. The id is the digits after the LAST separator (_ - or space),
   * so names that themselves contain underscores or digits still work.
   */
  private parseEntityParam(raw: string): { name: string; id: number } | null {
    let value = raw;
    try { value = decodeURIComponent(raw); } catch { /* already decoded, or has a stray % */ }

    const match = value.trim().match(/^(.*)[\s_-](\d+)$/);
    if (!match) return null;

    const id = Number(match[2]);
    if (!id || isNaN(id)) return null;

    return { name: match[1], id };
  }

  /**
   * Makes names comparable: lowercases and removes everything that is not a letter
   * or number (spaces, _, -, ., ', ( ) etc.). Works for Hindi / Marathi too.
   */
  private normalizeName(value: string): string {
    return (value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]/gu, '');
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // LOAD ENTITY
  // ══════════════════════════════════════════════════════════════════════════════
  private loadEntityData(): void {
    this.isLoading = true;
    const notFound = () => this.router.navigate(['/PageNotFound']);

    // Shared "did the API return an entity (and, if asked, does its link name match the URL)?" check
    const accept = (
      res: any,
      nameOf: (d: any) => string | undefined,
      assign: (d: any) => void,
      linkNameOf?: (d: any) => string | undefined
    ) => {
      const decrypted = this.encryptionService.decryptResponse(res);
      const data = decrypted?.data || decrypted?.item || decrypted;
      const apiName = nameOf(data) || '';

      // The id identifies the entity; make sure the API really returned one.
      if (!apiName) {
        notFound();
        return;
      }

      // Optional name check against the entity's "link name" (otherDetail1: plain words + spaces).
      // If that value is empty on the record, the check is skipped.
      const linkName = linkNameOf ? (linkNameOf(data) || '').trim() : '';
      if (linkName && this.normalizeName(linkName) !== this.normalizeName(this.moduleName)) {
        console.warn('[self-onboarding] name mismatch', { fromUrl: this.moduleName, otherDetail1: linkName });
        notFound();
        return;
      }
      assign(data);
      this.isLoading   = false;
      this.currentStep = 'role';
    };

    if (this.module === 'hospital') {
      const payload: HospitalMasterReadByIdRequestDTO = { hospitalId: this.moduleId };
      this.signedCall(environment.hospitalExternalApiKey, '/ReadById_External', payload,
        (enc, sig) => this.hospitalMasterService.HospitalMaster_ExternalReadById(
          enc as unknown as HospitalMasterReadByIdRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => accept(res, d => d?.hospitalName, d => {
            this.hospitalData = d as HospitalMasterDTO;
            this.loadDepartments();
          }),
          error: notFound
        });

    } else if (this.module === 'school') {
      const payload: SchoolMasterReadByIdRequestDTO = { schoolId: this.moduleId };
      this.signedCall(environment.schoolExternalApiKey, '/ReadById_External', payload,
        (enc, sig) => this.schoolMasterService.SchoolMaster_ExternalReadById(
          enc as unknown as SchoolMasterReadByIdRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => accept(res, d => d?.schoolName, d => (this.schoolData = d as SchoolMasterDTO)),
          error: notFound
        });

    } else if (this.module === 'society') {
      const payload: SocietyReadByIdRequestDTO = { societyId: this.moduleId };
      this.signedCall(environment.societyExternalApiKey, '/ReadById_External', payload,
        (enc, sig) => this.societyService.Society_ExternalReadById(
          enc as unknown as SocietyReadByIdRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => accept(res, d => d?.societyName, d => {
            this.societyData = d as SocietyDTO;
            this.loadBuildings();
          }, d => d?.otherDetail1),
          error: notFound
        });

    } else {
      notFound();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // GETTERS (entity info for the template)
  // ══════════════════════════════════════════════════════════════════════════════
  get entityName(): string {
    if (this.module === 'hospital') return this.hospitalData?.hospitalName || '';
    if (this.module === 'school')   return this.schoolData?.schoolName     || '';
    return (this.societyData as any)?.societyName || '';
  }

  get entityLogo(): string {
    if (this.module === 'hospital') return (this.hospitalData as any)?.logo || (this.hospitalData as any)?.profileImage || '';
    if (this.module === 'school')   return (this.schoolData as any)?.logo   || (this.schoolData as any)?.profileImage   || '';
    return (this.societyData as any)?.logo || '';
  }

  get entityAddress(): string {
    if (this.module === 'hospital') return [this.hospitalData?.address, this.hospitalData?.city, this.hospitalData?.state].filter(Boolean).join(', ');
    if (this.module === 'school')   return [(this.schoolData as any)?.address, (this.schoolData as any)?.city].filter(Boolean).join(', ');
    return [(this.societyData as any)?.address, (this.societyData as any)?.city].filter(Boolean).join(', ');
  }

  get entityContact(): string {
    if (this.module === 'hospital') return (this.hospitalData as any)?.mobileNo || (this.hospitalData as any)?.contactNo || '';
    if (this.module === 'school')   return (this.schoolData as any)?.mobileNo || '';
    return (this.societyData as any)?.mobileNo || '';
  }

  get entityEmail(): string {
    if (this.module === 'hospital') return (this.hospitalData as any)?.emailId || '';
    if (this.module === 'school')   return (this.schoolData as any)?.emailId   || '';
    return (this.societyData as any)?.emailId || '';
  }

  get moduleIcon(): string {
    if (this.module === 'hospital') return '🏥';
    if (this.module === 'school')   return '🏫';
    return '🏘️';
  }

  get isStudentAdmission(): boolean {
    return this.module === 'school' && this.selectedRole === 'StudentAdmission';
  }

  get isFlatMember(): boolean {
    return this.module === 'society' && this.selectedRole === 'FlatMember';
  }

  /**
   * The designation that is sent to the API.
   *  - Flat member: Owner / Co-Owner / the "Other" text (defaults to "Flat Member")
   *  - Everyone else: whatever was set, falling back to the role value
   */
  get resolvedDesignation(): string {
    if (this.isFlatMember) {
      if (this.form.designationType === 'Other') {
        return this.form.designationOther.trim() || DEFAULT_FLAT_DESIGNATION_TEXT;
      }
      return this.form.designationType;
    }
    return this.form.designation || this.selectedRole;
  }

  /** Flat members are not asked for address / PIN, so those are always sent empty */
  private get payloadAddress(): string { return this.isFlatMember ? '' : this.form.address; }
  private get payloadPinCode(): string { return this.isFlatMember ? '' : this.form.pinCode; }

  // ══════════════════════════════════════════════════════════════════════════════
  // WIZARD NAVIGATION
  // ══════════════════════════════════════════════════════════════════════════════
  goBack(): void {
    if (this.currentStep === 'form') this.currentStep = 'role';
  }

  selectRole(role: string): void {
    const changed = this.selectedRole !== role;
    this.selectedRole = role;

    if (changed) {
      // The number being verified can differ between student and staff roles
      this.resetOtp();
      this.mobileTouched = false;
      this.form.classId  = 0;
      this.form.flatId   = 0;          // flat only applies to flat members
      this.form.designationType  = 'Other';
      this.form.designationOther = DEFAULT_FLAT_DESIGNATION_TEXT;
    }

    if (this.isStudentAdmission && !this.classes.length) {
      this.loadClasses();
    }

    // Building may already be picked from an earlier role: make sure its flats are available
    if (this.isFlatMember && this.form.buildingId && !this.flats.length) {
      this.loadFlats(this.form.buildingId);
    }

    this.currentStep = 'form';
  }

  /** Flat member designation picker */
  pickDesignation(type: FlatDesignation): void {
    this.form.designationType = type;
    if (type === 'Other' && !this.form.designationOther.trim()) {
      this.form.designationOther = DEFAULT_FLAT_DESIGNATION_TEXT;
    }
  }

  /** Moves the cursor spotlight on a role card (CSS reads --mx / --my) */
  spot(event: MouseEvent): void {
    const el   = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    el.style.setProperty('--my', `${event.clientY - rect.top}px`);
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // FORM PROGRESS (0 - 100) for the live completion bar
  // ══════════════════════════════════════════════════════════════════════════════
  get formProgress(): number {
    const f = this.form;
    const checks: boolean[] = [
      !!f.firstName.trim(),
      !!f.lastName.trim(),
      this.isMobileValid,
      this.otpVerified
    ];
    if (this.isStudentAdmission) checks.push(!!f.classId);
    if (this.isFlatMember)       checks.push(!!f.buildingId, !!f.flatId);

    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // CLASSES
  // ══════════════════════════════════════════════════════════════════════════════
  private loadClasses(): void {
    if (!this.moduleId) return;
    this.classesLoading = true;

    const payload = { schoolId: this.moduleId, pageSize: 100, pageNo: 1 };

    this.signedCall(environment.schoolExternalApiKey, '/ReadBySchoolId_External', payload,
      (enc, sig) => this.classMasterService.ClassMaster_ReadBySchoolId_External(
        enc as unknown as any,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const decrypted = this.encryptionService.decryptResponse(res);
          this.classes = decrypted?.items || decrypted?.data?.items || decrypted?.data || [];
          this.classesLoading = false;
        },
        error: () => {
          this.classesLoading = false;
          this.toastr.warning('Could not load classes. Please try again.');
        }
      });
  }

  pickClass(classId: number): void {
    this.form.classId = classId;
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // HOSPITAL DEPARTMENTS
  // ══════════════════════════════════════════════════════════════════════════════
  private loadDepartments(): void {
    if (!this.moduleId) return;
    this.departmentsLoading = true;

    const payload = { hospitalId: this.moduleId, pageSize: 100, pageNo: 1 };

    this.signedCall(environment.hospitalExternalApiKey, '/ReadByHospitalId_External', payload,
      (enc, sig) => this.departmentMasterService.DepartmentMaster_ReadByHospitalId_External(
        enc as unknown as any,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const decrypted = this.encryptionService.decryptResponse(res);
          this.departments = decrypted?.items || decrypted?.data?.items || decrypted?.data || [];
          this.departmentsLoading = false;
        },
        error: () => {
          this.departmentsLoading = false;
        }
      });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // SOCIETY BUILDINGS & FLATS
  // ══════════════════════════════════════════════════════════════════════════════
  private loadBuildings(): void {
    if (!this.moduleId) return;
    this.buildingsLoading = true;

    const payload = { societyId: this.moduleId, pageSize: 100, pageNo: 1 };

    this.signedCall(environment.societyExternalApiKey, '/ReadBySocietyId_External', payload,
      (enc, sig) => this.buildingService.Building_ReadBySocietyId_External(
        enc as unknown as any,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const decrypted = this.encryptionService.decryptResponse(res);
          this.buildings = decrypted?.items || decrypted?.data?.items || decrypted?.data || [];
          this.buildingsLoading = false;
        },
        error: () => {
          this.buildingsLoading = false;
        }
      });
  }

  onBuildingChange(buildingId: any): void {
    const bId = Number(buildingId) || 0;
    this.form.buildingId = bId;
    this.form.flatId = 0;
    this.flats = [];

    // Only flat members pick a flat, so only they need the flats list
    if (!bId || !this.isFlatMember) return;

    this.loadFlats(bId);
  }

  private loadFlats(buildingId: number): void {
    this.flatsLoading = true;
    const payload = { buildingId, pageSize: 200, pageNo: 1 };

    this.signedCall(environment.societyExternalApiKey, '/ReadByBuildingId_External', payload,
      (enc, sig) => this.flatService.Flat_ReadByBuildingId_External(
        enc as unknown as any,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          // Ignore a late response if the user already switched building
          if (this.form.buildingId !== buildingId) return;
          const decrypted = this.encryptionService.decryptResponse(res);
          this.flats = decrypted?.items || decrypted?.data?.items || decrypted?.data || [];
          this.flatsLoading = false;
        },
        error: () => {
          this.flatsLoading = false;
        }
      });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // MOBILE + OTP (all inline on the form step)
  // ══════════════════════════════════════════════════════════════════════════════
  onMobileInput(event: Event): void {
    if (this.otpVerified) return;

    const input  = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    input.value  = digits;

    const changed = digits !== this.mobileValue;
    this.mobileValue = digits;

    // Number edited after an OTP was sent: that OTP no longer applies
    if (changed && this.otpSent) this.clearOtpState();
  }

  sendOtp(): void {
    this.mobileTouched = true;
    if (!this.isMobileValid || this.isSendingOtp || this.otpTimer > 0 || this.otpVerified) return;

    this.isSendingOtp = true;

    // Raw payload: {"mobileNo":"...","emailId":"..."}  (emailId only when the user entered one)
    const payload: GenerateOtpRequest = { mobileNo: this.mobileValue };
    const email = (this.form.emailId || '').trim();
    if (email && !this.isStudentAdmission) payload.emailId = email;

    this.http.post<GenerateOtpResponse>(UserLogin_GenerateMobileOtp, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isSendingOtp = false;

          if (res?.statusCode !== 200) {
            this.toastr.error(res?.message || 'Failed to send OTP. Please try again.');
            return;
          }

          this.otpSent      = true;
          this.otpError     = false;
          this.otpDigits    = Array(this.otpLength).fill('');
          this.startOtpTimer();
          this.toastr.success(res.message || 'OTP sent successfully!');
          this.focusOtpBox(0);
        },
        error: (err) => {
          this.isSendingOtp = false;
          this.toastr.error(err?.error?.message || 'Failed to send OTP. Please try again.');
        }
      });
  }

  /** "Change" next to the Verified badge: unlock the number and start over */
  resetOtp(): void {
    this.otpVerified = false;
    this.clearOtpState();
  }

  private clearOtpState(): void {
    clearInterval(this.otpIntervalId);
    this.otpTimer       = 0;
    this.otpSent        = false;
    this.otpError       = false;
    this.isVerifyingOtp = false;
    this.otpDigits      = Array(this.otpLength).fill('');
  }

  private startOtpTimer(): void {
    clearInterval(this.otpIntervalId);
    this.otpTimer = 60;
    this.otpIntervalId = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) clearInterval(this.otpIntervalId);
    }, 1000);
  }

  // ── OTP boxes ───────────────────────────────────────────────────────────────
  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '');
    this.otpError = false;

    if (!value) {
      this.otpDigits[index] = '';
      input.value = '';
      return;
    }

    // One digit typed, or several at once (SMS autofill / paste into a box)
    this.fillFrom(index, value);
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const boxes = this.otpBoxes?.toArray() || [];

    switch (event.key) {
      case 'Backspace':
        if (!this.otpDigits[index] && index > 0) {
          event.preventDefault();
          this.otpDigits[index - 1] = '';
          boxes[index - 1].nativeElement.value = '';
          this.focusOtpBox(index - 1);
        }
        this.otpError = false;
        break;
      case 'ArrowLeft':
        if (index > 0) { event.preventDefault(); this.focusOtpBox(index - 1); }
        break;
      case 'ArrowRight':
        if (index < this.otpLength - 1) { event.preventDefault(); this.focusOtpBox(index + 1); }
        break;
      case 'Enter':
        event.preventDefault();
        break;
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!text) return;
    this.otpError = false;
    this.fillFrom(0, text);
  }

  /** Writes digits into the boxes starting at `start`, moves focus, auto-verifies when full */
  private fillFrom(start: number, digits: string): void {
    const boxes = this.otpBoxes?.toArray() || [];
    let i = start;

    for (const ch of digits) {
      if (i >= this.otpLength) break;
      this.otpDigits[i] = ch;
      if (boxes[i]) boxes[i].nativeElement.value = ch;
      i++;
    }

    if (i < this.otpLength) {
      this.focusOtpBox(i);
    } else {
      boxes[this.otpLength - 1]?.nativeElement.blur();
    }

    if (this.otpDigits.every(d => !!d)) this.verifyOtp();
  }

  private focusOtpBox(index: number): void {
    setTimeout(() => this.otpBoxes?.get(index)?.nativeElement.focus());
  }

  private verifyOtp(): void {
    if (this.isVerifyingOtp || this.otpVerified) return;

    this.isVerifyingOtp = true;
    this.otpError = false;

    // Raw payload: {"mobileNo":"...","otp":"..."}
    const payload: ValidateOtpRequest = { mobileNo: this.mobileValue, otp: this.otpDigits.join('') };

    this.http.post<ValidateOtpResponse>(UserLogin_ValidateMobileOtp, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isVerifyingOtp = false;

          if (res?.statusCode !== 200) {
            this.onOtpRejected();
            return;
          }

          this.otpVerified = true;
          clearInterval(this.otpIntervalId);
          this.otpTimer = 0;
          this.toastr.success(res.message || 'Mobile number verified!');
        },
        error: () => {
          this.isVerifyingOtp = false;
          this.onOtpRejected();
        }
      });
  }

  /** Wrong / expired code: shake, then clear the boxes for another try */
  private onOtpRejected(): void {
    this.otpError = true;

    // Let the shake play, then clear the boxes
    setTimeout(() => {
      const boxes = this.otpBoxes?.toArray() || [];
      this.otpDigits = Array(this.otpLength).fill('');
      boxes.forEach(x => (x.nativeElement.value = ''));
      this.focusOtpBox(0);
    }, 450);
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // SUBMIT STATE
  // ══════════════════════════════════════════════════════════════════════════════
  get canSubmit(): boolean {
    return this.submitHint === '';
  }

  /** Empty string = ready. Otherwise tells the user what is still missing. */
  get submitHint(): string {
    const f = this.form;
    if (this.isFlatMember && !f.buildingId)        return 'Select your building / wing.';
    if (this.isFlatMember && !f.flatId)            return 'Select your flat.';
    if (!f.firstName.trim() || !f.lastName.trim()) return 'Enter the first and last name.';
    if (this.isStudentAdmission && !f.classId)     return 'Pick a class to continue.';
    if (!this.isMobileValid)                       return 'Enter a valid mobile number.';
    if (!this.otpVerified)                         return 'Verify your mobile number with the OTP to continue.';
    return '';
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // MAIN SUBMIT
  //   • School · StudentAdmission  -> StudentAdmission_Create_External (single call)
  //   • Everything else            -> UserMaster_Create_External -> UAM Create_External
  // ══════════════════════════════════════════════════════════════════════════════
  submitOnboarding(): void {
    if (this.isSubmitting) return;

    if (!this.canSubmit) {
      this.mobileTouched = true;
      this.toastr.warning(this.submitHint);
      return;
    }

    this.isSubmitting = true;

    if (this.isStudentAdmission) {
      this.submitStudentAdmission();
    } else {
      this.submitUserAndAffiliation();
    }
  }

  // ── Student admission: one call, no userMaster / UAM ────────────────────────
  private submitStudentAdmission(): void {
    const payload = this.buildStudentAdmissionPayload();

    this.signedCall(environment.schoolExternalApiKey, '/Create_External', payload,
      (enc, sig) => this.studentAdmissionService.StudentAdmission_ExternalCreate(
        enc as unknown as StudentAdmissionCreateRequestDTO,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.onSuccess(),
        error: (err) => this.fail(err, 'Failed to submit admission request. Please try again.')
      });
  }

  /**
   * Same payload shape as the admin "Student Admission" form (submitForm), so the
   * same DTO / stored procedure accepts it. Optional identity numbers go as NULL
   * (never '') to protect the unique Aadhaar / PAN indexes.
   */
  private buildStudentAdmissionPayload(): StudentAdmissionCreateRequestDTO {
    const f            = this.form;
    const year         = new Date().getFullYear();
    const guardianName = `${f.parentFirstName} ${f.parentLastName}`.trim();

    return {
      // ── Student ──
      sUserId:      0,
      firstName:    f.firstName.trim(),
      middleName:   f.middleName.trim(),
      lastName:     f.lastName.trim(),
      profileImg:   '',
      dob:          f.dob ? new Date(f.dob) : null,
      gender:       f.gender,
      bloodGroup:   '',
      mobileNo:     '',
      emailId:      '',
      aadharNumber: null as any,
      panNumber:    null as any,
      nationality:  'Indian',
      religion:     '',
      caste:        '',
      motherTongue: '',
      address:      f.address,
      country:      'India',
      state:        '',
      city:         '',
      pinCode:      f.pinCode,
      specialNeeds: '',
      disabilityStatus: '',

      // ── Admission ──
      schoolId:          this.moduleId,
      academicYear:      `${year}-${year + 1}`,
      classId:           f.classId,
      admissionCategory: 'General',
      admissionStatus:   'Pending',
      applicationDate:   new Date(),
      approvedBy:        0,
      approvedDate:      null,
      remarks:           '',
      admissionDate:     null,
      admissionMedium:   '',
      admissionType:     'New',
      admissionBoard:    '',

      previousSchoolName:    '',
      previousSchoolAddress: '',
      previousMedium:        '',
      previousLastClass:     '',
      previousMarksObtained: 0,
      previousBoard:         '',
      reasonForTransfer:     '',
      transferCertificateNo: '',
      tcDate:                null,

      // ── Primary guardian (the verified parent) ──
      pgRelation:     'Guardian',
      pgUserId:       0,
      pgName:         guardianName,
      pgdob:          null,
      pgProfileImg:   '',
      pgEducation:    '',
      pgMobileNo:     f.parentMobile,
      pgOccupation:   '',
      pgDesignation:  '',
      pgAnnualIncome: '0',
      pgEmailId:      '',
      pgAadharNumber: null as any,
      pgPanNumber:    null as any,

      // ── Secondary guardian (not collected on self-onboarding) ──
      sgRelation:     '',
      sgUserId:       0,
      sgName:         '',
      sgdob:          null,
      sgProfileImg:   '',
      sgEducation:    '',
      sgMobileNo:     '',
      sgOccupation:   '',
      sgDesignation:  '',
      sgAnnualIncome: '0',
      sgEmailId:      '',
      sgAadharNumber: null as any,
      sgPanNumber:    null as any,

      otherDetail1: '',
      otherDetail2: '',
      otherDetail3: '',
      otherDetail4: '',
      otherDetail5: 'SELF_ONBOARDING',
      otherDetail6: '',
      actionUser:   'SELF_ONBOARDING'
    };
  }

  // ── Staff / members: UserMaster -> UAM ──────────────────────────────────────
  private submitUserAndAffiliation(): void {
    const userPayload: UserMasterCreateRequestDTO = {
      firstName:    this.form.firstName.trim(),
      middleName:   this.form.middleName.trim(),
      lastName:     this.form.lastName.trim(),
      dob:          this.form.dob ? new Date(this.form.dob) : new Date('2000-01-01'),
      mobileNo:     this.mobileValue,
      emailId:      this.form.emailId || '',
      designation:  this.resolvedDesignation,
      profileImage: '',
      address:      this.payloadAddress,
      gender:       this.form.gender,
      pinCode:      this.payloadPinCode,
      otherDetail1: 'SELF_ONBOARDED',
      otherDetail2: this.module.toUpperCase(),
      otherDetail3: String(this.moduleId),
      otherDetail4: this.selectedRole,
      actionUser:   'SELF_ONBOARDING'
    };

    this.signedCall(environment.masterExternalApiKey, '/Create_External', userPayload,
      (enc, sig) => this.usersService.UserMaster_Create_External(
        enc as unknown as UserMasterCreateRequestDTO,
        sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (userRes) => {
          const decrypted = this.encryptionService.decryptResponse(userRes);
          const data = decrypted?.data || decrypted?.item || decrypted;
          const userId = data?.userId || (userRes as any)?.userId || (userRes as any)?.data?.userId || 0;
          this.createdUserId = userId;
          this.createAffiliation(userId);
        },
        error: (err) => this.fail(err, 'Failed to create user. Please try again.')
      });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // CREATE AFFILIATION (hospital / school staff / society)
  // ══════════════════════════════════════════════════════════════════════════════
  private createAffiliation(userId: number): void {
    const now         = new Date();
    const mobile      = this.mobileValue;
    const dob         = this.form.dob ? new Date(this.form.dob) : undefined;
    const designation = this.resolvedDesignation;
    const middleName  = this.form.middleName.trim();
    const address     = this.payloadAddress;
    const pinCode     = this.payloadPinCode;

    if (this.module === 'hospital') {
      const payload: HospitalUserAffiliationCreateRequestDTO = {
        userId,
        hospitalId:      this.moduleId,
        affiliationType: this.selectedRole,
        designation,
        isPrimary:       1,
        joinedOn:        now,
        firstName:       this.form.firstName.trim(),
        middleName,
        lastName:        this.form.lastName.trim(),
        mobileNo:        mobile,
        emailId:         this.form.emailId,
        address,
        pinCode,
        gender:          this.form.gender,
        dob,
        department:      this.form.department,
        specialization:  this.form.specialization,
        otherDetail1:    'Pending',          // VerificationStatus
        otherDetail2:    'SELF_ONBOARDING',
        actionUser:      'SELF_ONBOARDING'
      } as HospitalUserAffiliationCreateRequestDTO;

      this.signedCall(environment.hospitalExternalApiKey, '/Create_External', payload,
        (enc, sig) => this.hospitalUAMService.HospitalUserAffiliationCreate_External(
          enc as unknown as HospitalUserAffiliationCreateRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.affiliationObserver());

    } else if (this.module === 'school') {
      // StudentAdmission never reaches here (it has its own API), but the map is kept for safety
      const roleIdMap: Record<string, number> = {
        TeachingStaff:    17,
        NonTeachingStaff: 18,
        Drivers:          19,
        StudentAdmission: 16
      };
      const payload: UserAffiliationMappingCreateRequestDTO = {
        userId,
        schoolId:        this.moduleId,
        affiliationType: this.selectedRole,
        designation,
        occupation:      this.selectedRole,
        income:          0,
        isPrimary:       1,
        joinedOn:        now,
        assignedRoleId:  roleIdMap[this.selectedRole] || 17,
        isActive:        1,
        firstName:       this.form.firstName.trim(),
        middleName,
        lastName:        this.form.lastName.trim(),
        mobileNo:        mobile,
        emailId:         this.form.emailId,
        address,
        pinCode,
        gender:          this.form.gender,
        dob,
        otherDetail1:    'Pending',
        otherDetail2:    'SELF_ONBOARDING',
        otherDetail3:    '',
        otherDetail4:    '',
        otherDetail5:    '',
        otherDetail6:    '',
        actionUser:      'SELF_ONBOARDING'
      } as UserAffiliationMappingCreateRequestDTO;

      this.signedCall(environment.schoolExternalApiKey, '/Create_External', payload,
        (enc, sig) => this.schoolUAMService.UserAffiliationMapping_Create_External(
          enc as unknown as UserAffiliationMappingCreateRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.affiliationObserver());

    } else if (this.module === 'society') {
      const payload: SMUserAffiliationMappingCreateRequestDTO = {
        userId,
        societyId:          this.moduleId,
        buildingId:         this.form.buildingId || 0,
        flatId:             this.isFlatMember ? (this.form.flatId || undefined) : undefined,
        affiliationType:    this.selectedRole,
        designation,        // Owner / Co-Owner / "Flat Member" (or custom text) for flat members
        isPrimary:          1,
        joinedOn:           now,
        firstName:          this.form.firstName.trim(),
        middleName,
        lastName:           this.form.lastName.trim(),
        mobileNo:           mobile,
        emailId:            this.form.emailId,
        address,
        pinCode,
        gender:             this.form.gender,
        dob,
        verificationStatus: 'Pending',
        otherDetail1:       'Pending',
        otherDetail2:       'SELF_ONBOARDING',
        actionUser:         'SELF_ONBOARDING'
      } as SMUserAffiliationMappingCreateRequestDTO;

      this.signedCall(environment.societyExternalApiKey, '/Create_External', payload,
        (enc, sig) => this.societyUAMService.SmUserAffiliationMapping_Create_External(
          enc as unknown as SMUserAffiliationMappingCreateRequestDTO,
          sig.xApiKey, sig.xApiTimestamp, sig.xApiNonce, sig.xApiSignature))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.affiliationObserver());
    }
  }

  /** Same success/error handling for all three modules */
  private affiliationObserver() {
    return {
      next: () => this.onSuccess(),
      error: (err: any) => this.fail(err, 'Failed to register affiliation. Please try again.')
    };
  }

  private onSuccess(): void {
    this.isSubmitting = false;
    this.buildConfetti();
    this.currentStep = 'success';

    // Make sure the success message + download buttons are in view (long forms scroll on phones)
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Phones: show the message briefly, then open the right store automatically
    this.startStoreRedirect();
  }

  private fail(err: any, fallback: string): void {
    this.isSubmitting = false;
    this.toastr.error(err?.error?.message || err?.message || fallback);
  }

  /** Pieces for the burst behind the success check (CSS reads --x --y --r --c --d) */
  private buildConfetti(): void {
    const colors = ['#fe5325', '#b5005f', '#011044', '#16a34a', '#fbbf24', '#38bdf8'];
    const count  = 18;

    this.confetti = Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const dist  = 70 + Math.random() * 60;
      return {
        x: Math.round(Math.cos(angle) * dist),
        y: Math.round(Math.sin(angle) * dist),
        r: Math.round(Math.random() * 360),
        c: colors[i % colors.length],
        d: Math.round(Math.random() * 200)
      };
    });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Encrypts the payload, generates the API signature, then runs the actual request.
   * Wrapped in defer() so an encryption error surfaces as a normal observable error.
   */
  private signedCall<T>(
    apiKey: string,
    pathAndQuery: string,
    payload: unknown,
    request: (enc: EncryptedExternalBody, sig: SignatureResponseDTO) => Observable<T>
  ): Observable<T> {
    return defer(() => {
      const enc = this.createEncryptedBody(payload);
      const sigPayload: SignatureRequestDTO = {
        apiKey,
        privateKeyPem: environment.privateKeyPem,
        method: 'POST',
        pathAndQuery,
        body: JSON.stringify(enc)
      };

      return this.userPermissionService.UserPermission_generateSignature(sigPayload).pipe(
        switchMap((sigRes: any) => request(enc, this.extractSig(sigRes)))
      );
    });
  }

  private createEncryptedBody(payload: unknown): EncryptedExternalBody {
    const enc: any = this.encryptionService.createEncryptedRequest(payload);
    const body: EncryptedExternalBody = {
      EncData:  enc?.EncData  || enc?.encData  || '',
      TokenKey: enc?.TokenKey || enc?.tokenKey || ''
    };
    if (!body.EncData || !body.TokenKey) {
      throw new Error('Encryption service did not return EncData and TokenKey.');
    }
    return body;
  }

  private extractSig(response: any): SignatureResponseDTO {
    const d = response?.data || response?.item || response?.result || response;
    return {
      xApiKey:       d?.xApiKey       || d?.XApiKey       || '',
      xApiTimestamp: d?.xApiTimestamp || d?.XApiTimestamp || '',
      xApiNonce:     d?.xApiNonce     || d?.XApiNonce     || '',
      xApiSignature: d?.xApiSignature || d?.XApiSignature || '',
      body:          d?.body          || ''
    };
  }
}